import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { LoadableDataState } from "@my-dld/utils/UIUtils";
import { TCCommercialInfo } from "@eres/ejari-api/Entity/tenant";
import React, { Component } from "react";
import { TenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import _ from "lodash";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import Strings from "@my-dld/assets/strings";
import FormSection from "@my-dld/components/Form/FormSection";
import PickerInput from "@eres/forms-web/PickerInput";
import { IdExtractor, lookupConverter } from "@my-dld/utils/LookupUtil";
import FormRow from "@eres/forms-web/FormRow";
import {
	AlphaSortProcessor,
	isGovernmentAuthority,
	isLicStateAuthority,
	LicenseAuthoritiesEmiratesTransformer,
	LicSourceProcessor,
	populateLookupOnTo,
	StateConsumer
} from "@eres/ejari-api/Utils";
import { EjariLookUp } from "@eres/ejari-api/constants";
import { EjariPropertyUsage, Lookup } from "@eres/ejari-api/Entity";
import { dualDataManager } from "@eres/forms/valuemanager/DualDataManager";
import TextInputField from "@eres/forms-web/TextInputField";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../utils";
import { navigate } from "@my-dld/utils/StoreUtils";
import { RenderActions } from "./ContractInfo";
import FormMandatory from "../FormMandatory";

interface Props extends RoutedViewProps {

}

interface LookupState {
	Emirates?: Lookup[];
	Authorities?: Lookup[];
	LicenseStates?: Lookup[];
}

interface State extends LookupState, LoadableDataState {
	newData?: TCCommercialInfo;
	orgData?: TCCommercialInfo;
	isFromContext?: boolean;
	showState: boolean;
	isGovCompany: boolean;
}

class CommercialInfo extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			errorContainer: {},
			loadState: "loading",
			showState: false,
			isGovCompany: false,
		};
	}

	componentDidMount(): void {
		const { contract, commercialInfo } = this.context;
		const orgCommercialInfo: TCCommercialInfo = {};
		if ( contract ) {
			if ( contract.Tenants && contract.Tenants.length > 0 ) {
				const person = contract.Tenants[ 0 ].Person;
				if ( person ) {
					orgCommercialInfo.LicenseIssuerEmirate = person.LicenseIssuerEmirate;
					orgCommercialInfo.LicenseIssuer = Number( person.LicenseIssuer );
					orgCommercialInfo.LicenseStateId = person.LicenseStateId;
					orgCommercialInfo.LicenseNo = person.LicenseNo;
				}
			}
		}
		const state = {
			orgData: orgCommercialInfo || {},
			newData: commercialInfo || {},
			isFromContext: ( commercialInfo !== undefined || contract !== undefined ),
		};
		this.setState( state );
		populateLookupOnTo( EjariLookUp.EMIRATES, StateConsumer( this, "Emirates" ), LicenseAuthoritiesEmiratesTransformer, LicSourceProcessor );
		populateLookupOnTo( EjariLookUp.LICENSE_STATES, StateConsumer( this, "LicenseStates" ), undefined, AlphaSortProcessor );
	}

	updateFields = ( authId: number ) => {
		const { Authorities } = this.state;
		if ( Authorities ) {
			const Authority = Authorities.find( x => ( x.Id + "" ) === authId + "" );
			if ( Authority ) {
				this.setState( {
					showState: isLicStateAuthority( Authority ),
					isGovCompany: isGovernmentAuthority( Authority ),
				} );
			} else {
				this.setState( {
					showState: false,
					isGovCompany: false,
				} );
			}
		}

	};

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		const pNewData = prevState.newData;
		const newData = this.state.newData;
		if ( ( prevState.Emirates !== this.state.Emirates ) ||
			( newData && newData.LicenseIssuerEmirate && newData.LicenseIssuerEmirate !== pNewData?.LicenseIssuerEmirate ) ) {
			this.updateLicenseAuthorities();
		}
		if ( newData && pNewData && ( this.state.Authorities !== prevState.Authorities || !_.isEqual( newData.LicenseIssuer, pNewData.LicenseIssuer ) ) ) {
			this.updateFields( newData.LicenseIssuer! );
		}
	}

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
			errorManager: defaultErrorManager,
			valueManager: dualDataManager,
		};
		const { Emirates, Authorities, LicenseStates, showState, isGovCompany } = this.state;
		const { action } = this.context;
		const data = this.getUpdatedData();
		return <div className="tender-registration-form">
			<FormSection title={ Strings.commercial_details }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<PickerInput
						required={ true }
						id={ "LicenseIssuerEmirate" }
						label={ Strings.lic_issuer_emirate }
						isLoading={ Emirates === undefined }
						items={ Emirates }
						toIdentifier={ IdExtractor }
						valueConverter={ lookupConverter }/>
					{ data.LicenseIssuerEmirate && <PickerInput
						required={ true }
						id={ "LicenseIssuer" }
						label={ Strings.lic_issuer_authority }
						isLoading={ Authorities === undefined }
						items={ Authorities }
						toIdentifier={ IdExtractor }
						valueConverter={ lookupConverter }/> }
					{ showState &&
						<PickerInput
							required={ true }
							id={ "LicenseStateId" }
							label={ Strings.license_state }
							isLoading={ LicenseStates === undefined }
							items={ LicenseStates }
							toIdentifier={ IdExtractor }
							valueConverter={ lookupConverter }/>
					}
					{ !isGovCompany &&
						<TextInputField
							required={ true }
							id={ "LicenseNo" }
							label={ Strings.lic_number }
						/>
					}
				</FormRow>
				<FormRow { ...filedProps }>
					<RenderActions
						action={ action! } onContinue={ this.onContinue }
						onSave={ this.onSave } onBack={ this.onBack }/>
				</FormRow>
			</FormSection>
		</div>;
	}

	private getUpdatedData = ( state: State = this.state ) => {
		const data: TCCommercialInfo = {};
		_.merge( data, state.orgData, state.newData );
		return data;
	};

	private updateLicenseAuthorities() {
		const { isFromContext, Emirates } = this.state;
		const data = this.getUpdatedData();
		if ( Emirates && data.LicenseIssuerEmirate ) {
			const selEmirate = Emirates?.find( ( emirate ) => emirate.Id === data.LicenseIssuerEmirate );
			this.setState( {
				Authorities: selEmirate?.Children,
				newData: {
					...this.state.newData,
					LicenseIssuer: isFromContext ? data.LicenseIssuer : undefined,
				},
				isFromContext: false,
			} );
		}
	}

	private isValid() {
		const data = this.getUpdatedData();
		const { isGovCompany, showState } = this.state;
		const error: ErrorContainer = {};
		let isValid = true;
		if ( data.LicenseIssuerEmirate === undefined ) {
			isValid = false;
			error.LicenseIssuerEmirate = Strings.required;
		}
		if ( data.LicenseIssuer === undefined ) {
			isValid = false;
			error.LicenseIssuer = Strings.required;
		}
		if ( showState ) {
			if ( data.LicenseStateId === undefined ) {
				isValid = false;
				error.LicenseStateId = Strings.required;
			}
		}
		if ( !isGovCompany ) {
			isValid = checkNonEmpty( data, "LicenseNo", error ) && isValid;
		}
		this.setState( {
			errorContainer: error,
		} );
		return isValid;
	}

	private onContinue = () => {
		if ( this.isValid() ) {
			const { property } = this.context;
			let data = this.getUpdatedData();
			let m: TCCommercialInfo = {};
			m.LicenseIssuerEmirate = data.LicenseIssuerEmirate;
			m.LicenseIssuer = data.LicenseIssuer;
			if ( this.state.showState ) {
				m.LicenseStateId = data.LicenseStateId;
			}
			if ( !this.state.isGovCompany ) {
				m.LicenseNo = data.LicenseNo;
			}
			updateTenantContractContext( {
				commercialInfo: m,
			} );
			if ( property && property.EjariPropertyUsageId === EjariPropertyUsage.INDUSTRIAL ) {
				navigate( "hazards" );
			} else {
				navigate( "documents" );
			}
		}
	};

	private onSave = () => {
		if ( this.isValid() ) {
			let { contract } = this.context;
			const { isGovCompany, showState } = this.state;
			if ( contract && contract.Tenants && contract.Tenants.length > 0 ) {
				const tenant = contract.Tenants[ 0 ];
				if ( tenant && tenant.Person ) {
					const data = this.getUpdatedData();
					let person = tenant.Person;
					person.LicenseIssuerEmirate = data.LicenseIssuerEmirate;
					person.LicenseIssuer = "" + data.LicenseIssuer;
					if ( showState ) {
						person.LicenseStateId = data.LicenseStateId;
					}
					if ( !isGovCompany ) {
						person.LicenseNo = data.LicenseNo;
					}

					contract = {
						...contract,
						Tenants: [ tenant ],
					};
					updateTenantContractContext( { contract: contract, commercialInfo: data, canResubmit: true } );
				}
			}
			this.onBack();
		}
	};

	private onBack = () => {
		this.props.history.goBack();
	};
}

export default CommercialInfo;
